import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  ArrowSquareOut,
  CaretRight,
  ChatCenteredText,
  CheckCircle,
  CircleNotch,
  GitBranch,
  GitPullRequest,
  type Icon,
  TrashSimple,
  Warning,
  XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@alfred/convex/_generated/api";
import type { Doc } from "@alfred/convex/_generated/dataModel";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { cn, relativeTime } from "~/lib/utils";

export const Route = createFileRoute("/pr-reviews")({
  component: PrReviewsPage,
});

// Binary verdict surface: comment is no longer produced by the engine.
// Older rows in Convex still carry `verdict: "comment"`; we render those
// with a muted "Legacy" badge so the history stays visible without
// suggesting `comment` is a current option.
type VerdictKind = "approve" | "request_changes" | "comment";

const VERDICT_META: Record<
  VerdictKind,
  {
    label: string;
    badge: "success" | "warn" | "danger" | "info" | "muted";
    icon: Icon;
  }
> = {
  approve: { label: "Approve", badge: "success", icon: CheckCircle },
  request_changes: { label: "Request changes", badge: "danger", icon: Warning },
  comment: { label: "Comment (legacy)", badge: "muted", icon: ChatCenteredText },
};

function PrReviewsPage() {
  const reviews = useQuery(api.prReviews.list, {});

  return (
    <div className="flex h-full min-h-0 flex-col">
      <header className="border-b border-border/70 bg-background/85 px-4 py-4 backdrop-blur lg:px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <GitPullRequest size={18} weight="duotone" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-semibold tracking-tight">PR Reviews</h1>
            <p className="text-xs text-muted-foreground">
              Auto-generated PR reviews. Triggered once per PR when the
              configured GitHub login is added as a requested reviewer. Uses
              the tech brief and per-repo memories as context.
            </p>
          </div>
          <div className="ml-auto text-xs text-muted-foreground">
            {reviews ? (
              <>
                <span className="font-semibold text-foreground">
                  {reviews.length}
                </span>{" "}
                review{reviews.length === 1 ? "" : "s"}
              </>
            ) : (
              "Loading…"
            )}
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 overflow-y-auto px-4 py-4 lg:px-6">
        {reviews === undefined ? (
          <div className="mx-auto max-w-4xl space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-2xl" />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="mx-auto max-w-4xl space-y-3">
            {reviews.map((r) => (
              <ReviewCard key={r._id} review={r} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="grid h-full place-items-center px-6 text-center">
      <div className="max-w-md">
        <GitPullRequest
          size={48}
          weight="duotone"
          className="mx-auto mb-3 text-muted-foreground"
        />
        <h2 className="text-base font-semibold">No PR reviews yet</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          When a human adds the configured PR-reviewer login (e.g.{" "}
          <code className="rounded bg-muted px-1 py-0.5 font-mono">ptsadi</code>
          ) as a reviewer on any PR in the org, Alfred picks it up on the
          next dispatcher tick, runs a one-shot supervisor review (Opus
          xhigh, with the tech brief + per-repo memories in context), and
          posts the result as a GitHub PR review. Each PR is reviewed at
          most once.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          Configure the trigger login via{" "}
          <code className="rounded bg-muted px-1 py-0.5 font-mono">
            ALFRED_PR_REVIEWER_LOGIN
          </code>{" "}
          in your engine env.
        </p>
      </div>
    </div>
  );
}

/**
 * Render the review duration as `Xs` / `Xm Ys` / `Xh Ym`.
 *
 * - For `succeeded` / `failed` rows we use the persisted `durationMs`
 *   (set by the `finish` mutation), falling back to `finishedAt - claimedAt`
 *   for legacy rows that pre-date the field.
 * - For `reviewing` rows we tick every 5s so the counter stays roughly
 *   live without doing a re-render storm.
 */
function DurationBadge({ review }: { review: Doc<"prReviews"> }) {
  const isInflight = review.status === "reviewing";
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    if (!isInflight) return;
    const handle = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(handle);
  }, [isInflight]);

  let ms: number | undefined;
  if (review.durationMs !== undefined) {
    ms = review.durationMs;
  } else if (review.finishedAt) {
    ms = review.finishedAt - review.claimedAt;
  } else if (isInflight) {
    ms = now - review.claimedAt;
  }
  if (ms === undefined) return null;

  return (
    <span
      title={isInflight ? "Time elapsed so far" : "How long this review took"}
      className={cn(
        isInflight && ms > 5 * 60_000 && "text-[color:var(--brand-orange)]",
      )}
    >
      · {isInflight ? "running " : "took "}
      {formatDuration(ms)}
    </span>
  );
}

function formatDuration(ms: number): string {
  const totalSec = Math.max(0, Math.round(ms / 1000));
  if (totalSec < 60) return `${totalSec}s`;
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes < 60) {
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remMin = minutes % 60;
  return remMin > 0 ? `${hours}h ${remMin}m` : `${hours}h`;
}

function ReviewCard({ review }: { review: Doc<"prReviews"> }) {
  const [expanded, setExpanded] = React.useState(false);
  const remove = useMutation(api.prReviews.remove);

  const verdict = (review.verdict as VerdictKind | undefined) ?? null;
  const meta = verdict ? VERDICT_META[verdict] : null;
  const Icon = meta?.icon;

  const isReviewing = review.status === "reviewing";
  const isFailed = review.status === "failed";

  return (
    <Card
      className={cn(
        "overflow-hidden",
        isFailed && "border-[color:var(--status-error)]/40",
        isReviewing && "border-[color:var(--status-info)]/40",
      )}
    >
      <CardHeader className="flex flex-row items-start gap-3 space-y-0 pb-2">
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
            isReviewing && "bg-[color:var(--status-info)]/15 text-[color:var(--status-info)]",
            isFailed && "bg-[color:var(--status-error)]/15 text-[color:var(--status-error)]",
            !isReviewing && !isFailed && verdict === "approve" && "bg-[color:var(--status-success)]/15 text-[color:var(--status-success)]",
            !isReviewing && !isFailed && verdict === "request_changes" && "bg-[color:var(--status-error)]/15 text-[color:var(--status-error)]",
            !isReviewing && !isFailed && verdict === "comment" && "bg-[color:var(--status-info)]/15 text-[color:var(--status-info)]",
            !isReviewing && !isFailed && !verdict && "bg-muted text-muted-foreground",
          )}
        >
          {isReviewing ? (
            <CircleNotch size={18} weight="bold" className="animate-spin" />
          ) : isFailed ? (
            <XCircle size={18} weight="duotone" />
          ) : Icon ? (
            <Icon size={18} weight="duotone" />
          ) : (
            <GitPullRequest size={18} weight="duotone" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <CardTitle className="text-sm leading-snug">
            <a
              href={review.prUrl}
              target="_blank"
              rel="noreferrer"
              className="text-foreground hover:text-primary hover:underline"
            >
              {review.prTitle || `#${review.prNumber}`}
            </a>
          </CardTitle>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
            <span className="inline-flex items-center gap-1 font-mono">
              <GitBranch size={11} weight="bold" />
              {review.repoFullName}#{review.prNumber}
            </span>
            {review.prAuthor ? <span>by @{review.prAuthor}</span> : null}
            {review.jiraKey ? (
              <Link
                to="/tickets/$key"
                params={{ key: review.jiraKey }}
                className="text-primary hover:underline"
              >
                {review.jiraKey}
              </Link>
            ) : null}
            <span>
              · {review.status === "reviewing" ? "started" : "reviewed"}{" "}
              {relativeTime(review.finishedAt ?? review.claimedAt)}
            </span>
            <DurationBadge review={review} />
            {review.score !== undefined ? (
              <span>· confidence {Math.round(review.score * 100)}%</span>
            ) : null}
            {review.attempts && review.attempts > 1 ? (
              <span title={
                review.lastTriggeredBy
                  ? `last re-review requested by @${review.lastTriggeredBy}`
                  : "re-reviewed"
              }>
                · {review.attempts} passes
                {review.lastTrigger === "comment"
                  ? " (re-review on comment)"
                  : review.lastTrigger === "review-requested"
                    ? " (re-requested)"
                    : ""}
              </span>
            ) : null}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isReviewing ? (
            <Badge variant="info">Reviewing…</Badge>
          ) : isFailed ? (
            <Badge variant="danger">Failed</Badge>
          ) : meta ? (
            <Badge variant={meta.badge}>{meta.label}</Badge>
          ) : (
            <Badge variant="muted">{review.status}</Badge>
          )}
        </div>
      </CardHeader>

      {(review.summary || isFailed) && (
        <CardContent className="space-y-2 pt-0">
          {isFailed ? (
            <p className="line-clamp-3 text-xs text-[color:var(--status-error)]">
              {review.error || "(no error message)"}
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-foreground/90">
              {review.summary}
            </p>
          )}

          {(review.mustFix?.length || review.niceToHave?.length || review.notes) ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              <CaretRight
                size={12}
                weight="bold"
                className={cn(
                  "transition-transform",
                  expanded ? "rotate-90" : "",
                )}
              />
              {expanded ? "Hide details" : "Show details"}
            </button>
          ) : null}

          {expanded ? (
            <div className="space-y-3 rounded-xl border border-border/70 bg-muted/30 p-3 text-xs">
              {review.mustFix && review.mustFix.length > 0 ? (
                <ReviewList
                  title="Must fix"
                  items={review.mustFix}
                  variant="danger"
                />
              ) : null}
              {review.niceToHave && review.niceToHave.length > 0 ? (
                <ReviewList
                  title="Nice to have"
                  items={review.niceToHave}
                  variant="muted"
                />
              ) : null}
              {review.notes ? (
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Notes
                  </p>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
                    {review.notes}
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-2 pt-1">
            <a
              href={review.prUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent"
            >
              <ArrowSquareOut size={11} weight="bold" /> Open PR
            </a>
            {review.postedReviewUrl ? (
              <a
                href={review.postedReviewUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2 py-1 text-[11px] font-medium text-foreground hover:bg-accent"
              >
                <ArrowSquareOut size={11} weight="bold" /> View posted review
              </a>
            ) : null}
            <span className="ml-auto text-[10px] text-muted-foreground">
              triggered by @{review.reviewerLogin}
            </span>
            <button
              type="button"
              onClick={() => {
                if (
                  confirm(
                    `Delete this review record for ${review.repoFullName}#${review.prNumber}? This frees the lock so Alfred can re-review on the next tick if the login is still a requested reviewer.`,
                  )
                ) {
                  remove({ id: review._id })
                    .then(() => toast.success("Review record deleted"))
                    .catch((err) => toast.error(err.message));
                }
              }}
              className="rounded-md p-1 text-muted-foreground hover:bg-[color:var(--status-error)]/15 hover:text-[color:var(--status-error)]"
              title="Delete record (allows a re-review)"
            >
              <TrashSimple size={12} weight="bold" />
            </button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}

function ReviewList({
  title,
  items,
  variant,
}: {
  title: string;
  items: string[];
  variant: "danger" | "muted";
}) {
  return (
    <div>
      <p
        className={cn(
          "mb-1 text-[10px] font-semibold uppercase tracking-wider",
          variant === "danger"
            ? "text-[color:var(--status-error)]"
            : "text-muted-foreground",
        )}
      >
        {title}
      </p>
      <ul className="space-y-1 text-sm leading-relaxed text-foreground/90">
        {items.map((it, idx) => (
          <li key={idx} className="flex gap-2">
            <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-current opacity-60" />
            <span className="whitespace-pre-wrap">{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
